import { TypedMap } from 'flow/types/TypedMap';
import { ContactFull } from 'flow/types/Contact';
import { TelephoneNumber } from 'flow/types/TelephoneNumber';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import {
    AddressFields,
    ContactFieldOrder,
    ContactFieldType,
    NameFields,
} from 'flow/utils/contact-utils';
import {
    getAlt1PhoneNumberFromContact,
    getAlt2PhoneNumberFromContact,
    getFixedNumberFromContact,
    getMobileNumberFromContact,
    getPhoneNumberFromContact,
} from 'flow/utils/extension-utils';
import { getContactAddressLine } from 'flow/components/contact-card/contact-fields/contact-address-field-utils';

export type ContactFieldAction = { kind: 'call' | 'mail'; target: string };

export interface ContactCardField {
    key: string;
    label: string;
    value: string;
    action?: ContactFieldAction;
}

interface BuildArgs {
    contact?: TypedMap<ContactFull>;
    /** The ticket's number (`origin.visitorId`), shown as the mobile number
     *  when there's no contact, or the contact has neither a fixed nor a
     *  mobile number. Skipped when technical or withheld. */
    visitorNumber?: TypedMap<TelephoneNumber>;
    /** Fallback email used when the contact has none (unsaved contact, or an
     *  email visitor whose address only lives on the ticket origin). */
    email?: string;
    ownCountryCode: string;
    t: (key: string, fallback?: string) => string;
}

type AddressParts = { address?: string; postalcode?: string; city?: string };

/**
 * The visitor's number when it can be shown and dialled: not a technical
 * number, and not a withheld caller (`unknown` / `anonymous`) — the same
 * checks `ContactTitle` applies before it shows a number.
 */
const getShownVisitorNumber = (number?: TypedMap<TelephoneNumber>) => {
    if (number?.get('type') === 'technical_number') return undefined;
    const phoneNumber = number?.get('number');
    return phoneNumber === 'unknown' || phoneNumber === 'anonymous'
        ? undefined
        : phoneNumber;
};

const numberRow = (
    key: string,
    label: string,
    number: string,
    ownCountryCode: string,
): ContactCardField => ({
    key,
    label,
    value: prettyFormatE164Number(number, ownCountryCode),
    action: { kind: 'call', target: number },
});

const textRow = (
    key: string,
    label: string,
    value: string | undefined,
): ContactCardField[] => (value ? [{ key, label, value }] : []);

/**
 * Rows built from the contact's top-level fields, for contacts without an
 * editable DTO (e.g. an unsaved caller). Follows the order of
 * `ContactInfoFieldsFallback`: the main fields, then the two alternative
 * numbers, then each of the contact's `additionalFields` under its own key.
 */
const buildFallbackFields = ({
    contact,
    visitorNumber,
    email,
    ownCountryCode,
    t,
}: BuildArgs): ContactCardField[] => {
    const fixedNumber =
        getFixedNumberFromContact(contact) ||
        getPhoneNumberFromContact(contact);
    const mobileNumber = getMobileNumberFromContact(contact);
    // No number of its own: show the ticket's number (e.g. the visitor's).
    const fallbackMobile =
        !fixedNumber && !mobileNumber
            ? getShownVisitorNumber(visitorNumber)
            : undefined;
    const mobile = mobileNumber || fallbackMobile;
    const mail = contact?.get('email') || email;
    const altNumbers: [string, string, string | null][] = [
        [
            'altTelephone1',
            t('ContactCard.Items.AltPhone1', 'Phone number #2'),
            getAlt1PhoneNumberFromContact(contact),
        ],
        [
            'altTelephone2',
            t('ContactCard.Items.AltPhone2', 'Phone number #3'),
            getAlt2PhoneNumberFromContact(contact),
        ],
    ];
    const additionalFields =
        contact?.get('additionalFields')?.entrySeq().toArray() ?? [];

    return [
        ...(fixedNumber
            ? [
                  numberRow(
                      'fixed',
                      t('ContactCard.Items.FixedPhone', 'Fixed number'),
                      fixedNumber,
                      ownCountryCode,
                  ),
              ]
            : []),
        ...(mobile
            ? [
                  numberRow(
                      'mobile',
                      t('ContactCard.Items.MobilePhone', 'Mobile number'),
                      mobile,
                      ownCountryCode,
                  ),
              ]
            : []),
        ...(mail
            ? [
                  {
                      key: 'email',
                      label: t('ContactCard.Items.Email', 'Email'),
                      value: mail,
                      action: { kind: 'mail', target: mail } as const,
                  },
              ]
            : []),
        ...textRow(
            'companyName',
            t('ContactCard.Items.Company', 'Company'),
            contact?.get('companyName'),
        ),
        ...textRow(
            'department',
            t('ContactCard.Items.Department', 'Department'),
            contact?.get('department'),
        ),
        ...textRow(
            'occupation',
            t('ContactCard.Items.Occupation', 'Title'),
            contact?.get('occupation'),
        ),
        ...textRow(
            'address',
            t('ContactCard.Address.Title', 'Address'),
            contact
                ? getContactAddressLine({
                      address: contact.get('address'),
                      postalcode: contact.get('postalcode'),
                      city: contact.get('city'),
                  })
                : undefined,
        ),
        ...textRow(
            'country',
            t('ContactCard.Items.Country', 'Country'),
            contact?.get('country'),
        ),
        ...textRow(
            'description',
            t('ContactCard.Items.Description', 'Description'),
            contact?.get('description'),
        ),
        ...textRow(
            'keywords',
            t('ContactCard.Items.Keywords', 'Search words'),
            contact?.get('keywords'),
        ),
        ...altNumbers.flatMap(([key, label, number]) =>
            number ? [numberRow(key, label, number, ownCountryCode)] : [],
        ),
        ...additionalFields.flatMap(([key, value]) =>
            textRow(`additional-${key}`, key, value),
        ),
    ];
};

/**
 * Every populated field from the saved contact's editable DTO, in the
 * canonical order; numbers get a call action, emails a mail action. Name
 * fields are left out (the summary card shows the name) and the address
 * parts are combined into one row. Falls back to the contact's top-level
 * fields, then the visitor's number/email, when there's no editable DTO.
 *
 * Pure (no hooks) so callers can wrap it in `useMemo` — actions are described
 * declaratively (`{ kind, target }`) and wired up by the row component.
 */
export const buildContactFields = (args: BuildArgs): ContactCardField[] => {
    const { contact, ownCountryCode, t } = args;
    const contactFields = contact?.getIn([
        'editableContactDTO',
        'contactFields',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ]) as any;

    if (!contactFields || typeof contactFields.forEach !== 'function') {
        return buildFallbackFields(args);
    }

    const rows: ContactCardField[] = [];
    const addressParts: AddressParts = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contactFields.forEach((data: any, key: string) => {
        const lowerKey = key.toLowerCase();
        if (NameFields.includes(lowerKey) || key.startsWith('_')) return;

        const label = data.get('label') || key;
        const type = data.get('fieldType');
        if (AddressFields.includes(lowerKey)) {
            addressParts[lowerKey as keyof AddressParts] = data.get('value');
        } else if (ContactFieldType.NUMBER.is(type)) {
            const number = data.getIn(['number', 'number']);
            if (number)
                rows.push(numberRow(key, label, number, ownCountryCode));
        } else if (ContactFieldType.EMAIL.is(type)) {
            const value = data.get('value');
            if (value) {
                rows.push({
                    key,
                    label,
                    value,
                    action: { kind: 'mail', target: value },
                });
            }
        } else if (ContactFieldType.BASIC_STRING.is(type)) {
            // Other field types are not rendered, as in ContactInfoFields.
            const value = data.get('value');
            if (value) rows.push({ key, label, value });
        }
    });

    // One row for street + postcode + city, always under the generic address
    // label (the street field's own label would mislabel the combined row).
    const addressLine = getContactAddressLine(addressParts);
    if (addressLine) {
        rows.push({
            key: 'address',
            label: t('ContactCard.Address.Title', 'Address'),
            value: addressLine,
        });
    }

    const orderIndex = (key: string) => {
        const i = ContactFieldOrder.indexOf(key.toLowerCase());
        return i >= 0 ? i : ContactFieldOrder.size;
    };
    return rows.sort((a, b) => orderIndex(a.key) - orderIndex(b.key));
};
